import {observer} from 'mobx-react-lite'
import * as React from 'react'
import {useEffect} from 'react'
import {Lang} from 'yougile-lang'
import {Util} from 'yougile-util'
import {getNextTwoScheduledReadable, toServerSchedule} from 'yougile-model'
import {Button} from '@components/common/base/buttons'
import {
  textCursorSelectAll,
  useBoolean,
  useFocusState,
  useForceUpdate
} from '@containers/hooks'
import {cn} from '@containers/html-utils'
import {Help} from '../common/help/help'
import {repeatStore} from './repeat-store'
import {ScheduleEditor} from './schedule-editor'

export const RepeatEditTemplate = observer(() => {
  const current = repeatStore.state.current

  const titleValid = useBoolean(!!current?.title?.trim())
  const editorValid = useBoolean(true)

  const setCurrent = repeatStore.setCurrent
  const cancelEdit = repeatStore.cancelEditWithConfirm

  const forceUpdate = useForceUpdate()
  useEffect(() => {
    forceUpdate()
  }, [repeatStore.state.edited])

  useEffect(() => {
    repeatStore.setScheduleEditFormValid(editorValid.value && titleValid.value)
  }, [editorValid.value, titleValid.value])
  const [, setFocus] = useFocusState()

  if (!current) {
    return null
  }

  const bem = Util.bemFrom('repeat-edit')
  const onTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const title = e.target.value
    setCurrent({...current, title})
    titleValid.setValue(!!title.trim())
  }

  const isStopped = !current.schedule
  const start = () => {
    setCurrent({
      ...current,
      schedule:
        current?.savedSchedule ??
        toServerSchedule({type: 'daily', hour: 12, minute: 0})
    })
  }

  const onFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setFocus(true)()
    textCursorSelectAll(e)
  }

  return (
    <>
      <div className={bem('back')} onClick={cancelEdit}>
        {Lang.msg.repeat.backToTemplates}
      </div>
      <div className={bem('template-name-label')}>
        {Lang.msg.repeat.taskName}:
      </div>
      <input
        className={bem('template-name', {error: !titleValid.value})}
        value={current.title}
        placeholder={Lang.msg.repeat.namePlaceholder}
        onFocus={onFocus}
        onBlur={setFocus(false)}
        onChange={onTitleChange}
        autoFocus={!current.title}
      />
      {!titleValid.value && (
        <div className={bem('error')}>{Lang.msg.repeat.emptyNameError}</div>
      )}

      <div className={bem('edit-cnt')}>
        <div className={bem('edit-form')}>
          {isStopped ? (
            <div className={bem('stopped-cnt')}>
              <div className={bem('stopped-label')}>
                {Lang.msg.repeat.stopped}
              </div>
              <div className={bem('stopped-btn')} onClick={start}>
                {Lang.msg.repeat.start}
              </div>
            </div>
          ) : (
            <ScheduleEditor
              repeatTemplate={current}
              update={setCurrent}
              valid={editorValid}
            />
          )}
        </div>
        <div className={bem('v-delim')} />
        <div className={bem('edit-tpl')}>
          <div className={bem('edit-hint')}>
            <div className={bem('edit-hint-text')}>
              {Lang.msg.repeat.youCanEditTemplate}:
              <ul>
                <li>{Lang.msg.repeat.youCanEditTemplate1}</li>
                <li>{Lang.msg.repeat.youCanEditTemplate2}</li>
                <li>{Lang.msg.repeat.youCanEditTemplate3}</li>
                <li>{Lang.msg.repeat.youCanEditTemplate5}</li>
              </ul>
            </div>
            <Help name="editRepeatTemplate" />
          </div>
          <Button className="mt-16" onClick={repeatStore.openChat}>
            {Lang.msg.repeat.editTemplate} »
          </Button>
        </div>
      </div>
      <div className={cn(bem('next-repeat'), 'bg-main-even-old h-48')}>
        <div className={bem('next-repeat-label')}>
          {Lang.msg.repeat.taskWillAppear}:
        </div>
        <div className={bem('next-repeat-value')}>
          {getNextTwoScheduledReadable(current.schedule)}
        </div>
      </div>
    </>
  )
})
